/* eslint-disable react/prop-types */
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Droppable } from 'react-beautiful-dnd';
import styled from '@emotion/styled';
import UnifiedDayHeader from './UnifiedDayHeader';
import MonthDayContent from './MonthDayContent';
import Text from '../../../../shared/components/Text/Text';

const DayCell = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-width: 220px;
  background: white;
  border-radius: 4px;
  min-height: 200px;
  overflow: hidden;
  opacity: ${(props) => (props.isPaddingDay ? 0.5 : 1)};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: ${(props) => (props.isPaddingDay ? 0.7 : 1)};
  }
`;

const StatsBar = styled('div')`
  background: white;
  min-height: 40px;
  border-top: 1px solid #EEEEEE;
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
  margin-top: auto;
`;

const StatsBarContent = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 16px;
`;

const StatsBarItem = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

/**
 * Month-view day cell. Owns:
 *  - the rbd `Droppable` for `month-day-{date}`
 *  - a sticky `isInteractive` flag that flips the cell's items from
 *    plain `ReadOnlyExerciseItem` to `DraggableMonthExerciseItem` once
 *    the user hovers/focuses the cell. This avoids mounting hundreds
 *    of `Draggable` nodes across a dense month grid up front.
 */
const MonthDayDroppableCell = ({
  date,
  dayData,
  isPaddingDay,
  isMonthItemsLoading,
  programType,
  isAnalyticsDisabled,
}) => {
  const [isInteractive, setIsInteractive] = useState(false);
  const isDragInProgress = useSelector(
    (state) => state.calendar.data.dragIsInProgress,
  );

  /** Flip on first interaction; sticky for the lifetime of the cell. */
  const activate = useCallback(() => {
    if (!isInteractive) setIsInteractive(true);
  }, [isInteractive]);

  /** Activate during an in-flight drag too, so a cell can become a
   * destination even if the user hasn't hovered it before grabbing. */
  const shouldRenderDraggables = isInteractive || isDragInProgress;

  const dateKey = date.format('YYYY-MM-DD');
  const droppableId = `month-day-${dateKey}`;

  return (
    <DayCell
      isPaddingDay={isPaddingDay}
      onMouseEnter={activate}
      onFocusCapture={activate}
    >
      <UnifiedDayHeader
        programType={programType}
        viewType='monthView'
        date={date}
        isExpanded={false}
        onToggleExpand={null}
        showWorkoutName
        isWeekView={false}
      />
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <MonthDayContent
            date={date}
            exercises={dayData?.exercises || []}
            isLoading={isMonthItemsLoading}
            droppableProvided={provided}
            isInteractive={shouldRenderDraggables}
          />
        )}
      </Droppable>
      {!isAnalyticsDisabled && (
        <StatsBar>
          <StatsBarContent>
            <StatsBarItem>
              <Text fontSize='13px' color='#BDBDBD'>Sets</Text>
              <Text marginLeft='5px' marginTop='2px' fontSize='13px' color='#616161' fontWeight='500'>
                {dayData?.setCount || 0}
              </Text>
            </StatsBarItem>
            <StatsBarItem>
              <Text fontSize='13px' color='#BDBDBD'>Reps</Text>
              <Text marginLeft='5px' marginTop='2px' fontSize='13px' color='#616161' fontWeight='500'>
                {dayData?.repCount || 0}
              </Text>
            </StatsBarItem>
          </StatsBarContent>
        </StatsBar>
      )}
    </DayCell>
  );
};

export default React.memo(MonthDayDroppableCell);
